import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchModuleRows, fetchModuleSummary } from '../../lib/api/dashboard';
import { queryKeys } from '../../lib/query-client';
import { formatDate, formatNumber } from '../../lib/format';
import { useFilters } from '../../hooks/useFilters';
import { useTableState } from '../../hooks/useTableState';
import { isMetricValue, type IndicatorRow } from '../../types/domain';
import { DataBoundary } from '../../components/DataBoundary';
import { ChartCard } from '../../components/ChartCard';
import { DataTable, type Column } from '../../components/DataTable';
import { ExportMenu } from '../../components/ExportMenu';
import { KpiGrid } from '../../components/KpiGrid';
import { MetricDisplay } from '../../components/MetricDisplay';
import { ProvenanceBar } from '../../components/ProvenanceBar';
import { Card, ChartSkeleton, KpiSkeleton, Notice, TableSkeleton } from '../../components/primitives';
import { PageHeading } from '../layout/PageHeading';
import type { IndicatorColumnConfig, IndicatorModuleConfig } from './module-configs';

/**
 * Pantalla genérica de un módulo de indicadores: KPI + gráficos + tabla de detalle
 * paginada, ordenable y buscable.
 *
 * Todo llega calculado del backend. Aquí no se suma, no se promedia y no se
 * compara con ningún umbral.
 */
function renderCell(row: IndicatorRow, column: IndicatorColumnConfig): JSX.Element | string {
  const value = row[column.key];

  if (column.kind === 'metric') {
    if (isMetricValue(value)) {
      return (
        <MetricDisplay
          metric={value}
          format={column.metricFormat ?? 'decimal'}
          size="sm"
          inline
          hideNote
        />
      );
    }
    // Si el backend no envía el estado del dato, no se inventa una cifra.
    return <span className="muted">No disponible</span>;
  }

  if (value === null || value === undefined) return '—';
  if (isMetricValue(value)) {
    return <MetricDisplay metric={value} format="decimal" size="sm" inline hideNote />;
  }

  switch (column.kind) {
    case 'integer':
      return typeof value === 'number' ? formatNumber(value, 'integer') : String(value);
    case 'decimal':
      return typeof value === 'number' ? formatNumber(value, 'decimal') : String(value);
    case 'percent':
      return typeof value === 'number' ? formatNumber(value, 'percent') : String(value);
    case 'date':
      return typeof value === 'string' ? formatDate(value) : '—';
    case 'text':
      return typeof value === 'boolean' ? (value ? 'Sí' : 'No') : String(value);
  }
}

function plainValue(row: IndicatorRow, column: IndicatorColumnConfig): string | number | null {
  const value = row[column.key];
  if (value === null || value === undefined) return null;
  if (isMetricValue(value)) return value.value;
  if (typeof value === 'boolean') return value ? 'Sí' : 'No';
  return value;
}

export function IndicatorModulePage({ config }: { config: IndicatorModuleConfig }): JSX.Element {
  const { filters } = useFilters();
  const table = useTableState({ sortBy: config.defaultSort ?? null });

  const summaryQuery = useQuery({
    queryKey: queryKeys.moduleSummary(config.key, filters),
    queryFn: () => fetchModuleSummary(config.key, filters),
  });

  const rowsQuery = useQuery({
    queryKey: queryKeys.moduleRows(config.key, { filters, state: table.state }),
    queryFn: () => fetchModuleRows(config.key, filters, table.state),
  });

  const columns = useMemo<Array<Column<IndicatorRow>>>(
    () =>
      config.columns.map((column) => ({
        key: column.key,
        header: column.header,
        help: column.help,
        sortable: column.sortable,
        align: column.kind === 'text' || column.kind === 'date' ? 'left' : 'right',
        render: (row) => renderCell(row, column),
        plain: (row) => plainValue(row, column),
      })),
    [config.columns],
  );

  const summary = summaryQuery.data;
  const rows = rowsQuery.data;

  return (
    <>
      <PageHeading
        title={config.title}
        description={config.description}
        actions={<ExportMenu moduleKey={config.key} />}
      />

      {config.methodNote !== undefined && (
        <Notice tone="info">
          <strong>Nota metodológica.</strong> {config.methodNote}
        </Notice>
      )}

      <DataBoundary
        isLoading={summaryQuery.isLoading}
        isError={summaryQuery.isError}
        error={summaryQuery.error}
        onRetry={() => void summaryQuery.refetch()}
        isEmpty={summary !== undefined && summary.kpis.length === 0 && summary.charts.length === 0}
        empty={{
          title: 'Sin indicadores para este período',
          message:
            'No hay métricas calculadas para los filtros aplicados. Verifique el período o revise el estado de la sincronización.',
        }}
        warnings={summary?.provenance.warnings}
        coverage={summary?.provenance.coverage}
        skeleton={
          <>
            <KpiSkeleton count={4} />
            <div className="row row--2e">
              <ChartSkeleton />
              <ChartSkeleton />
            </div>
          </>
        }
      >
        {summary !== undefined && (
          <>
            <ProvenanceBar provenance={summary.provenance} />
            {summary.kpis.length > 0 && <KpiGrid kpis={summary.kpis} />}
            {summary.charts.length > 0 && (
              <div className="row row--2e">
                {summary.charts.map((chart) => (
                  <ChartCard key={chart.key} payload={chart} small />
                ))}
              </div>
            )}
          </>
        )}
      </DataBoundary>

      <Card title={config.tableTitle} flush>
        <DataBoundary
          isLoading={rowsQuery.isLoading}
          isError={rowsQuery.isError}
          error={rowsQuery.error}
          onRetry={() => void rowsQuery.refetch()}
          isEmpty={rows !== undefined && rows.total === 0 && table.state.search.length === 0}
          empty={{
            title: 'Sin filas para este período',
            message:
              'No hay detalle disponible con los filtros aplicados. Amplíe el período o limpie los filtros globales.',
          }}
          skeleton={<TableSkeleton columns={config.columns.length} />}
        >
          {rows !== undefined && (
            <DataTable
              caption={config.tableTitle}
              columns={columns}
              rows={rows.data}
              getRowId={(row) => row.id}
              state={table.state}
              onStateChange={table.setState}
              total={rows.total}
              totalPages={rows.totalPages}
              searchPlaceholder={config.searchPlaceholder}
              isFetching={rowsQuery.isFetching}
            />
          )}
        </DataBoundary>
      </Card>
    </>
  );
}
